import LinkIcon from '@mui/icons-material/Link';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, ListItemIcon } from '@mui/material';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import React, { useCallback, useState } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import {
  ServerPlatformUserRaw,
  useCurrentUserProfile,
} from 'src/graphql/entities/platform-user/platform-user';
import { useCopyToClipboard } from 'src/utils/hooks/use-copy-to-clipboard';

const SCHEDULE_URL = `${import.meta.env.VITE_REACT_APP_API_URL}/v2/signal-ics?`;

export interface ActionMenuItem<T> {
  label: string;
  onClick: (item: T) => void;
  icon?: React.JSX.Element;
  disabled?: boolean;
}

export const SignalOnCallScheduleManagementMenu = ({
  user,
  partnerId,
  onEditOptionClick,
}: {
  user?: ServerPlatformUserRaw;
  partnerId: number;
  onEditOptionClick?: (user: ServerPlatformUserRaw) => void;
}) => {
  const { copyToClipboard } = useCopyToClipboard();

  const { user: currentUser } = useCurrentUserProfile();

  const { groupRules } = useGroupRules();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyUrl = useCallback(() => {
    void copyToClipboard(
      user ? `${SCHEDULE_URL}userId=${user.id}` : `${SCHEDULE_URL}partnerId=${partnerId}`,
    );
  }, [copyToClipboard, partnerId, user]);

  const communicationPrefAvailable =
    user?.id === currentUser?.id || groupRules[UserGroupRules.AccessToUserManagementV2];

  const onEditHandler = useCallback(
    (user?: ServerPlatformUserRaw) => {
      if (user && onEditOptionClick) {
        onEditOptionClick(user);
      }
    },
    [onEditOptionClick],
  );

  const menuActions: ActionMenuItem<ServerPlatformUserRaw | undefined>[] = [
    {
      label: 'Schedule Feed URL',
      onClick: handleCopyUrl,
      icon: <LinkIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />,
    },
  ];

  if (user && onEditOptionClick && communicationPrefAvailable) {
    menuActions.unshift({
      label: 'Notification Preferences',
      onClick: onEditHandler,
      icon: <ManageAccountsIcon fontSize="small" />,
    });
  }

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              // maxHeight: DEFAULT_GRID_ROW_HEIGHT * 3,
              width: '30ch',
            },
          },
        }}
      >
        {menuActions.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            onClick={
              action.disabled
                ? undefined
                : () => {
                    setAnchorEl(null);
                    action.onClick(user);
                  }
            }
          >
            {!!action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
